import { useStore } from '@nanostores/react'
import { Dialog } from 'radix-ui'
import { atom } from 'nanostores'
import grantPermissionDialogHeaderImage from './grant-permission-dialog-header.png'

export interface GrantPermissionDialogMessages {
  dialog_trial_end_notification_title: () => string
  dialog_trial_end_notification_content: (args: { date: string }) => string
  btn_remind_me: () => string
  btn_no_need_reminder: () => string
}

export interface GrantPermissionDialogProps {
  requestNotificationPermission: () => Promise<void>
  m: GrantPermissionDialogMessages
  /** Controlled open state */
  open?: boolean
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void
  /** Trial expiration date (used in controlled mode) */
  expiresAt?: string
}

export function GrantPermissionDialog({
  m,
  open: controlledOpen,
  onOpenChange,
  expiresAt: controlledExpiresAt,
  ...props
}: GrantPermissionDialogProps) {
  const state = useStore($grantPermissionDialog)

  // Use controlled state if provided, otherwise use internal state
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : state.isOpen
  const expiresAt = isControlled ? (controlledExpiresAt ?? '') : state.expiresAt

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (!nextIsOpen) {
      if (isControlled) {
        onOpenChange?.(false)
      } else {
        grantPermissionDialog.close()
      }
    }
  }

  const handleConfirm = async () => {
    await props.requestNotificationPermission()
    if (isControlled) {
      onOpenChange?.(false)
    } else {
      grantPermissionDialog.close()
    }
  }

  const handleDismiss = () => {
    if (isControlled) {
      onOpenChange?.(false)
    } else {
      grantPermissionDialog.close()
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed top-1/2 left-1/2 z-50 w-[300px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg bg-white"
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          style={{
            ['--tw-enter-translate-x' as string]: '-50%',
            ['--tw-enter-translate-y' as string]: '-50%',
            ['--tw-exit-translate-x' as string]: '-50%',
            ['--tw-exit-translate-y' as string]: '-50%',
          }}
        >
          <img
            src={grantPermissionDialogHeaderImage}
            alt=""
            className="h-[140px] w-full object-cover"
          />

          <div className="flex flex-col items-center gap-4 px-5 pt-4 pb-5">
            <div className="flex flex-col items-center gap-3">
              <Dialog.Title className="text-center text-lg font-semibold text-[#333]">
                {m.dialog_trial_end_notification_title()}
              </Dialog.Title>

              <Dialog.Description className="text-center text-sm text-[#666]">
                {m.dialog_trial_end_notification_content({
                  date: formatReminderDate(expiresAt),
                })}
              </Dialog.Description>
            </div>

            <div className="flex w-full flex-col items-center gap-3">
              <button
                className="h-10 w-[250px] rounded bg-[#8EC925] text-base text-white"
                style={{
                  boxShadow: '0px 4px 0px 0px #4A8003',
                }}
                onClick={handleConfirm}
              >
                {m.btn_remind_me()}
              </button>

              <button
                className="h-9 text-base font-bold text-[#888]"
                onClick={handleDismiss}
              >
                {m.btn_no_need_reminder()}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

interface State {
  isOpen: boolean
  expiresAt: string
}

const initialState: State = {
  isOpen: false,
  expiresAt: '',
}

export const $grantPermissionDialog = atom<State>(initialState)

let resolvePromise: (() => void) | null = null

export const grantPermissionDialog = {
  /**
   * Opens the grant permission dialog and returns a promise that resolves when
   * the user either accepts or dismisses the dialog.
   */
  open: (expiresAt: string): Promise<void> => {
    return new Promise((resolve) => {
      resolvePromise = resolve
      $grantPermissionDialog.set({
        isOpen: true,
        expiresAt,
      })
    })
  },

  onConfirm: async (requestPermission: () => Promise<void>) => {
    await requestPermission()
    grantPermissionDialog.close()
  },

  onDismiss: () => {
    grantPermissionDialog.close()
  },

  close: () => {
    $grantPermissionDialog.set({
      ...$grantPermissionDialog.get(),
      isOpen: false,
    })
    resolvePromise?.()
    resolvePromise = null
  },
}

const REMINDER_OFFSET_MS = 48 * 60 * 60 * 1000 // 48 hours

function formatReminderDate(expiresAt: string): string {
  if (!expiresAt) return ''
  const expiresDate = new Date(expiresAt)
  const reminderDate = new Date(expiresDate.getTime() - REMINDER_OFFSET_MS)
  return new Intl.DateTimeFormat(navigator.language, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).format(reminderDate)
}
